import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import { authMode, isAuthConfigured, loginRequest, msalConfig, tokenRequest } from './authConfig';
import { AuthContext, type AuthStatus, type AuthUser } from './authContext';

const STUB_USER: AuthUser = {
  userId: 'local-dev-user',
  displayName: 'Local Actor',
  email: 'actor@localhost',
};

function toAuthUser(account: AccountInfo): AuthUser {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const email =
    (claims?.['email'] as string | undefined) ??
    (claims?.['preferred_username'] as string | undefined) ??
    account.username;
  return {
    // `oid` is stable per user per tenant; `localAccountId` mirrors it and is always present.
    userId: (claims?.['oid'] as string | undefined) ?? account.localAccountId,
    displayName: account.name ?? email ?? 'Actor',
    email: email ?? '',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const instanceRef = useRef<PublicClientApplication | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (authMode === 'stub') {
      setUser(STUB_USER);
      setStatus('authenticated');
      return;
    }

    if (!isAuthConfigured) {
      setError('Sign-in is not configured. Copy .env.example to .env and fill in the CIAM values.');
      setStatus('error');
      return;
    }

    const instance = new PublicClientApplication(msalConfig);
    instanceRef.current = instance;

    void (async () => {
      try {
        await instance.initialize();
        const redirect = await instance.handleRedirectPromise();
        if (cancelled) return;

        const account = redirect?.account ?? instance.getAllAccounts()[0] ?? null;
        if (account) {
          instance.setActiveAccount(account);
          setUser(toAuthUser(account));
          setStatus('authenticated');
        } else {
          setStatus('unauthenticated');
        }
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : 'Sign-in failed.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    if (authMode === 'stub') {
      setUser(STUB_USER);
      setStatus('authenticated');
      return;
    }
    const instance = instanceRef.current;
    if (!instance) return;
    // Redirect rather than popup: iOS Safari blocks popups from async handlers.
    await instance.loginRedirect(loginRequest);
  }, []);

  const signOut = useCallback(async () => {
    if (authMode === 'stub') {
      setUser(null);
      setStatus('unauthenticated');
      return;
    }
    const instance = instanceRef.current;
    if (!instance) return;
    await instance.logoutRedirect({ account: instance.getActiveAccount() ?? undefined });
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (authMode === 'stub') return null;
    const instance = instanceRef.current;
    const account = instance?.getActiveAccount();
    if (!instance || !account) return null;

    try {
      const result = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return result.accessToken;
    } catch (cause) {
      if (cause instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...tokenRequest, account });
        return null;
      }
      throw cause;
    }
  }, []);

  const value = useMemo(
    () => ({ status, user, error, signIn, signOut, getAccessToken }),
    [status, user, error, signIn, signOut, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
